/**
 * QuickTemplateSelector Component
 * Phase 5: Session Management UI (2026-01-19)
 *
 * Quick dropdown for selecting WhatsApp templates in the message composer.
 * Shows session state awareness and template categories.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
  Typography,
  Paper,
  Popper,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert
} from '@mui/material';
import {
  Description,
  Search,
  Clear,
  CheckCircle,
  Warning,
  Preview
} from '@mui/icons-material';
import api from '../../services/api';

interface WhatsAppTemplate {
  id: number;
  name: string;
  category: string;
  language_code: string;
  components: {
    header?: any;
    body?: string;
    footer?: string;
    buttons?: any[];
  };
  variables: string[];
  approval_status: string;
  quality_score?: string;
}

interface QuickTemplateSelectorProps {
  conversationId?: number;
  onSelectTemplate: (template: WhatsAppTemplate, variables: Record<string, string>) => void;
  sessionState?: 'ACTIVE_24H' | 'TEMPLATE_ONLY' | 'NO_RESPONSE' | 'CLOSED';
  disabled?: boolean;
}

const QuickTemplateSelector: React.FC<QuickTemplateSelectorProps> = ({
  conversationId,
  onSelectTemplate,
  sessionState = 'TEMPLATE_ONLY',
  disabled = false
}) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<WhatsAppTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [variableDialogOpen, setVariableDialogOpen] = useState(false);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await api.get('/campaigns/whatsapp-templates/');
        const fetchedTemplates = response.data.templates || [];
        setTemplates(fetchedTemplates);
        setFilteredTemplates(fetchedTemplates);
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Filter templates based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTemplates(templates);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = templates.filter(
      t =>
        t.name.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query)
    );
    setFilteredTemplates(filtered);
  }, [searchQuery, templates]);

  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'MARKETING':
        return 'primary';
      case 'UTILITY':
        return 'success';
      case 'AUTHENTICATION':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);

    // If template has variables, open dialog
    if (template.variables && template.variables.length > 0) {
      // Initialize variables with placeholders
      const initialVariables: Record<string, string> = {};
      template.variables.forEach((v, index) => {
        initialVariables[`${index + 1}`] = '';
      });
      setVariables(initialVariables);
      setVariableDialogOpen(true);
    } else {
      // No variables, select directly
      onSelectTemplate(template, {});
    }
  };

  // Handle variable submission
  const handleVariableSubmit = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate, variables);
      setVariableDialogOpen(false);
      setSelectedTemplate(null);
      setVariables({});
      setSearchQuery('');
    }
  };

  // Get template preview text
  const getTemplatePreview = (template: WhatsAppTemplate): string => {
    // Fix: components is an object, not an array
    const bodyText = template.components?.body;
    if (bodyText) {
      return bodyText.substring(0, 100) + (bodyText.length > 100 ? '...' : '');
    }
    return 'No preview available';
  };

  // Session state indicator
  const getSessionIndicator = () => {
    switch (sessionState) {
      case 'ACTIVE_24H':
        return (
          <Tooltip title="Active session - Free-form messages allowed">
            <CheckCircle fontSize="small" color="success" />
          </Tooltip>
        );
      case 'TEMPLATE_ONLY':
        return (
          <Tooltip title="Template required - Outside 24h window">
            <Warning fontSize="small" color="warning" />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Template Search/Select */}
      <Autocomplete
        options={filteredTemplates}
        getOptionLabel={(option) => option.name}
        loading={loading}
        disabled={disabled}
        value={null}
        inputValue={searchQuery}
        onInputChange={(_, value) => setSearchQuery(value)}
        onChange={(_, template) => {
          if (template) {
            handleTemplateSelect(template);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Search templates..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Description fontSize="small" color="action" />
                  {getSessionIndicator()}
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {searchQuery && (
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.name}
              </Typography>
              <Chip
                label={option.category}
                size="small"
                color={getCategoryColor(option.category)}
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
            >
              {getTemplatePreview(option)}
            </Typography>
          </Box>
        )}
        noOptionsText="No templates found"
        sx={{ minWidth: 250 }}
      />

      {/* Variable Input Dialog */}
      <Dialog
        open={variableDialogOpen}
        onClose={() => setVariableDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Configure Template Variables
          <Typography variant="subtitle2" color="text.secondary">
            {selectedTemplate?.name}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {selectedTemplate && (
            <>
              {/* Template Preview */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="caption">
                  <strong>Preview:</strong> {getTemplatePreview(selectedTemplate)}
                </Typography>
              </Alert>

              {/* Variable Inputs */}
              <Grid container spacing={2}>
                {selectedTemplate.variables.map((variable, index) => (
                  <Grid item xs={12} key={index}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Variable {{${index + 1}}} - ${variable}`}
                      placeholder={`Enter ${variable}`}
                      value={variables[`${index + 1}`] || ''}
                      onChange={(e) =>
                        setVariables(prev => ({
                          ...prev,
                          [`${index + 1}`]: e.target.value
                        }))
                      }
                      helperText={`This will replace {{${index + 1}}} in the template`}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Category Info */}
              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={selectedTemplate.category}
                  color={getCategoryColor(selectedTemplate.category)}
                  size="small"
                />
                <Chip
                  label={selectedTemplate.language_code}
                  variant="outlined"
                  size="small"
                />
                {selectedTemplate.approval_status === 'approved' && (
                  <Chip
                    icon={<CheckCircle />}
                    label="Approved"
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setVariableDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleVariableSubmit}
            disabled={
              selectedTemplate?.variables.some(
                (_, index) => !variables[`${index + 1}`]?.trim()
              )
            }
          >
            Use Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickTemplateSelector;
